/**
 * t627u: printable QR labels in the sizes people stick on kit: 76 × 51 mm and 150 × 100 mm thermal labels (one a
 * page, for label printers; t663u) and A4 Avery L7160 (21 a sheet) and L7163 (14 a sheet). Each label is just the QR
 * code (an opaque /qr/<code> link, nothing else in it) and the asset's details: a bold name, then short lines (ID,
 * make and model, building, and on the big label where it lives and its category), with the printed code in small
 * print so it can be typed in. No logo or workspace name (t664u). Any QR type can make labels: equipment is the first.
 */
import type { Equipment } from './equipment';
import { equipmentMakeModel } from './equipment';
import { fit, pdfAssemble, pdfText, qrLabelUrl, qrScanUrl, qrTypeName, type QrMatrix, type QrPoint } from './qrPoints';
import { formatReg } from '../fleet/logic';

export type QrLabelSize = '76x51' | '150x100' | 'L7160' | 'L7163';
export interface QrLabel { url: string; code: string; title: string; lines: string[] }
export interface QrLabelLayout {
  id: QrLabelSize; name: string; hint: string;
  /** mm */ pageW: number; pageH: number; labelW: number; labelH: number; left: number; top: number; pitchX: number; pitchY: number;
  cols: number; rows: number; pad: number;
  /** mm: the QR square (null = the label's height less padding) */ qr: number | null;
  /** pt */ title: number; text: number; small: number;
  /** how many detail lines fit under the name */ lines: number;
}
export const QR_LABEL_SIZES: QrLabelLayout[] = [
  { id: '76x51', name: '76 × 51 mm label', hint: 'Thermal label printer, one a label', pageW: 76, pageH: 51, labelW: 76, labelH: 51, left: 0, top: 0, pitchX: 76, pitchY: 51, cols: 1, rows: 1, pad: 3, qr: 36, title: 10, text: 7.5, small: 6, lines: 4 },
  { id: '150x100', name: '150 × 100 mm label', hint: 'Thermal label printer, one a label; bigger code, more details', pageW: 150, pageH: 100, labelW: 150, labelH: 100, left: 0, top: 0, pitchX: 150, pitchY: 100, cols: 1, rows: 1, pad: 6, qr: 70, title: 17, text: 12, small: 8.5, lines: 6 },
  { id: 'L7160', name: 'A4, Avery L7160', hint: '21 a sheet, 63.5 × 38.1 mm', pageW: 210, pageH: 297, labelW: 63.5, labelH: 38.1, left: 7.25, top: 15.15, pitchX: 66.04, pitchY: 38.1, cols: 3, rows: 7, pad: 3, qr: null, title: 8.5, text: 6.8, small: 5, lines: 3 },
  { id: 'L7163', name: 'A4, Avery L7163', hint: '14 a sheet, 99.1 × 38.1 mm', pageW: 210, pageH: 297, labelW: 99.1, labelH: 38.1, left: 4.65, top: 15.15, pitchX: 101.6, pitchY: 38.1, cols: 2, rows: 7, pad: 3, qr: null, title: 11, text: 8.5, small: 6, lines: 3 },
];
/** The default, and what a saved choice no longer offered (62 × 29, 50 × 25) becomes. */
export const QR_LABEL_DEFAULT: QrLabelSize = '76x51';
export const qrLabelLayout = (id: string | null | undefined): QrLabelLayout => QR_LABEL_SIZES.find(l => l.id === id) ?? QR_LABEL_SIZES[0]!;
/** A remembered size, or the default when it's unknown or retired. */
export const qrLabelSizeOr = (saved: string | null | undefined): QrLabelSize => (QR_LABEL_SIZES.some(l => l.id === saved) ? saved as QrLabelSize : QR_LABEL_DEFAULT);

/** The label for one piece of equipment: name; ID; make & model; building; then where it lives and its category
 *  (big labels only, as they fit). No type, no status. */
export function equipmentQrLabel(
  e: Pick<Equipment, 'name' | 'make' | 'model' | 'identifier' | 'location_name'> & Partial<Pick<Equipment, 'location_detail' | 'category_name'>>,
  code: string, origin: string,
): QrLabel {
  return {
    url: qrScanUrl(origin, code), code, title: e.name,
    lines: [e.identifier ? `ID ${e.identifier}` : '', equipmentMakeModel(e), e.location_name, e.location_detail?.trim() ?? '', e.category_name ?? ''].filter(Boolean),
  };
}

/**
 * Settings › QR codes: a label for any code from its list row: what it is, then where. Vehicles lead with the
 * registration; equipment printed from its own page uses `equipmentQrLabel` (make, model and ID too).
 */
export function qrPointLabel(p: Pick<QrPoint, 'code' | 'name' | 'action' | 'location_name' | 'location_detail' | 'template_name'> & Partial<Pick<QrPoint, 'vehicle_reg' | 'vehicle_name' | 'template_names' | 'target_name' | 'target_sub'>>, origin: string): QrLabel {
  const url = qrLabelUrl(origin, p);
  if (p.action === 'reception') return { url, code: p.code, title: 'Visitors', lines: [p.location_name, p.location_detail ?? '', 'Scan with your phone to sign in or out'].filter(Boolean) };
  if (p.action === 'vehicle') {
    return { url, code: p.code, title: p.vehicle_reg ? formatReg(p.vehicle_reg) : p.name, lines: [p.vehicle_name ?? '', p.location_name, 'Scan for the walkaround check or to report an issue'].filter(Boolean) };
  }
  if (p.action === 'badge') return { url, code: p.code, title: p.target_name ?? p.name, lines: [p.target_sub ?? '', p.location_name, 'Scan for their profile'].filter(Boolean) };
  const does = p.action === 'checklist' ? `Scan to start: ${p.template_name ?? 'the checklist'}`
    : p.action === 'place' ? (p.template_names?.length ? 'Scan to report an issue or start a checklist' : 'Scan to report an issue here')
    : p.action === 'issue' ? 'Scan to report an issue here'
    : p.action === 'equipment' ? 'Scan for checks or to report an issue'
    : p.action === 'document' ? 'Scan to read and acknowledge'
    : p.action === 'form' ? 'Scan to fill in the form'
    : p.action === 'training' ? 'Scan for the training'
    : p.target_name ? `Scan to open: ${p.target_name}` : `Scan: ${qrTypeName(p.action).toLowerCase()}`;
  return { url, code: p.code, title: p.name, lines: [p.location_name, p.location_detail ?? '', does].filter(Boolean) };
}

const MM = 72 / 25.4;
/** Splits a title into at most two lines that fit (the second ends in … when it's still too long). */
function wrap2(s: string, size: number, width: number): string[] {
  const per = size * 0.58, max = Math.max(1, Math.floor(width / per));
  if (s.length <= max) return [s];
  const cut = s.lastIndexOf(' ', max);
  const a = (cut > max / 3 ? s.slice(0, cut) : s.slice(0, max)).trimEnd();
  return [a, fit(s.slice(a.length).trimStart(), size, width, true)];
}

/** The labels as a PDF (bytes). Black on white only; Helvetica; no images; no dependencies. */
export function qrLabelsPdf(labels: QrLabel[], matrix: QrMatrix, size: QrLabelSize): Uint8Array {
  const L = qrLabelLayout(size);
  const W = L.pageW * MM, H = L.pageH * MM, per = L.cols * L.rows;
  const pages: string[] = [];
  for (let start = 0; start < Math.max(labels.length, 1); start += per) {
    const ops: string[] = ['0 g 0 G'];
    labels.slice(start, start + per).forEach((l, i) => {
      const x0 = (L.left + (i % L.cols) * L.pitchX) * MM, yTop = H - (L.top + Math.floor(i / L.cols) * L.pitchY) * MM;
      const pad = L.pad * MM, lh = L.labelH * MM, lw = L.labelW * MM;
      const qs = Math.min(L.qr != null ? L.qr * MM : Infinity, lh - 2 * pad);
      const mods = matrix(l.url);
      // The label's white margin is the quiet zone (a 1-module border inside the square as well). Centred top to bottom.
      const n = mods.length, quiet = 1, cell = qs / (n + 2 * quiet);
      const qx = x0 + pad, qy = yTop - lh / 2 - qs / 2;
      const rects: string[] = [];
      mods.forEach((row, ry) => row.forEach((on, rx) => {
        if (on) rects.push(`${(qx + (rx + quiet) * cell).toFixed(3)} ${(qy + qs - (ry + quiet + 1) * cell).toFixed(3)} ${cell.toFixed(3)} ${cell.toFixed(3)} re`);
      }));
      if (rects.length) ops.push(`${rects.join(' ')} f`);
      const tx = qx + qs + pad * 0.8, tw = x0 + lw - pad - tx;
      let y = yTop - pad - L.title * 0.85;
      const text = (s: string, sz: number, bold = false) => ops.push(`BT /${bold ? 'F2' : 'F1'} ${sz} Tf ${tx.toFixed(2)} ${y.toFixed(2)} Td (${pdfText(fit(s, sz, tw, bold))}) Tj ET`);
      for (const t of wrap2(l.title, L.title, tw)) { text(t, L.title, true); y -= L.title * 1.15; }
      y -= L.text * 0.25;
      for (const t of l.lines.slice(0, L.lines)) { text(t, L.text); y -= L.text * 1.25; }
      // The code at the foot, never cut short (people type it in when the camera can't read the square).
      y = yTop - lh + pad + L.small * 0.25;
      text(l.code, L.small);
    });
    pages.push(ops.join('\n'));
  }
  return pdfAssemble(pages, Number(W.toFixed(2)), Number(H.toFixed(2)));
}
